import UploadFileIcon from '@offline-mui/icons-material/UploadFile'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@offline-mui/material'
import { useEffect, useRef, useState } from 'react'
import { accountingRequest, formatMoney, jsonInit, when } from '../api'
import { CheckRow, fileBase64, problemFileBlob, readCheckFile } from '../checkFile'
import { DownloadLabel, downloadFrom } from '../download'
import { LeoHeader } from '../LeoHeader'
import { useLoggedInName } from '../loggedInUser'

// SND-14310: Invoice → Import Check. Upload reads the CSV or Excel file here and checks each row against Leo (nothing
// is written); Post client transactions adds one credit per row, only when no row has a problem (Rohit, 2026-10-29).
// Otherwise the problem file (Excel, problem rows in red with the reason) is downloaded, fixed and uploaded again.
// The import keeps the file (in Leo and in S3 under accounting/check-imports/) and the logged-in user as who posted it.



function headCell(label: string, right = false) {
  return (
    <TableCell key={label} align={right ? 'right' : 'left'} sx={{ color: 'common.white', fontWeight: 700, border: 0 }}>
      {label}
    </TableCell>
  )
}

function amountText(value: unknown) {
  const number = Number(value)
  return Number.isFinite(number) && String(value ?? '').trim() !== '' ? formatMoney(number) : String(value ?? '')
}

function RowsTable({ rows, showStatus }: { rows: any[]; showStatus: boolean }) {
  return (
    <TableContainer component={Card}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'info.main' }}>
            {[headCell('Line'), headCell('Office'), headCell('Invoice'), headCell('Date'), headCell('Reference'), headCell('FoP'), headCell('Amount', true), ...(showStatus ? [headCell('Status')] : [])]}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={`${row.line}-${index}`} sx={{ bgcolor: row.status === 'skipped' ? 'rgba(211, 47, 47, 0.06)' : undefined }}>
              <TableCell>{row.line}</TableCell>
              <TableCell>{row.office}</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {row.status === 'ready' || row.invoiceNumber ? (
                  <Link href={`/invoice/${encodeURIComponent(row.invoiceNumber || row.invoice)}`} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700 }}>
                    {row.invoiceNumber || row.invoice}
                  </Link>
                ) : row.invoice}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date}</TableCell>
              <TableCell>{row.reference}</TableCell>
              <TableCell>{row.fopCode}</TableCell>
              <TableCell align="right">{amountText(row.amount)}</TableCell>
              {showStatus ? (
                <TableCell>
                  {row.status === 'ready' ? (
                    <Chip size="small" color="success" label="Ready" />
                  ) : (
                    <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                      <Chip size="small" color="error" label="Problem" />
                      <Typography variant="caption" color="error.main">{row.reason}</Typography>
                    </Stack>
                  )}
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

// What an import posted and what it skipped (with the reason).
function ImportResult({ result }: { result: any }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Import {result.id} · {result.fileName} · posted {when(result.postedAt)} by {result.postedBy}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {result.postedCount} client transaction{result.postedCount === 1 ? '' : 's'} posted ({formatMoney(result.postedTotal)}) ·
        {' '}{result.skippedCount} skipped · {result.inS3 ? 'file in S3' : result.storageNote || 'file kept in Leo'}
      </Typography>
      <Typography variant="overline" color="info.main" sx={{ fontWeight: 700 }}>Posted</Typography>
      <RowsTable rows={(result.posted || []).map((row: any, index: number) => ({ ...row, line: index + 1, invoice: row.invoiceNumber, status: 'ready' }))} showStatus={false} />
      {result.skipped?.length ? (
        <>
          <Typography variant="overline" color="error.main" sx={{ display: 'block', fontWeight: 700, mt: 2 }}>Skipped</Typography>
          <RowsTable rows={result.skipped} showStatus />
        </>
      ) : null}
    </Box>
  )
}

export default function ImportCheckPage() {
  const input = useRef<HTMLInputElement | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [rows, setRows] = useState<CheckRow[]>([])
  const [preview, setPreview] = useState<any>(null)
  const [result, setResult] = useState<any>(null)
  const [imports, setImports] = useState<any[]>([])
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [asking, setAsking] = useState(false)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const loggedIn = useLoggedInName()
  const [name, setName] = useState('')
  const poster = loggedIn || name.trim()

  async function loadImports() {
    const body = await accountingRequest('/check-imports?limit=10')
    setImports(body.imports || [])
  }

  useEffect(() => {
    loadImports().catch((err) => setError(err.message))
  }, [])

  async function choose(event: any) {
    const picked: File | undefined = event.target.files?.[0]
    event.target.value = ''
    if (!picked) return
    setFile(picked)
    setRows([])
    setPreview(null)
    setResult(null)
    setError('')
    setBusy('preview')
    try {
      const read = await readCheckFile(picked)
      if (!read.length) throw new Error('The file has no rows.')
      setRows(read)
      setPreview(await accountingRequest('/check-imports/preview', jsonInit('POST', { rows: read })))
    } catch (err: any) {
      setError(err.message)
    } finally {
      setBusy('')
    }
  }

  async function post(event: any) {
    event.preventDefault()
    if (!file || poster.length < 2) return
    setBusy('post')
    setError('')
    try {
      const posted = await accountingRequest(
        '/check-imports',
        jsonInit('POST', { rows, postedBy: poster, fileName: file.name, contentType: file.type, fileBase64: await fileBase64(file) }),
      )
      setAsking(false)
      setResult(posted)
      setPreview(null)
      setRows([])
      setFile(null)
      await loadImports()
    } catch (err: any) {
      setAsking(false)
      setError(err.message)
    } finally {
      setBusy('')
    }
  }

  async function downloadProblems() {
    if (!file || !preview) return
    const problems = new Map<number, string>(
      (preview.rows || []).filter((row: any) => row.status !== 'ready').map((row: any) => [Number(row.line), row.reason]),
    )
    const url = URL.createObjectURL(await problemFileBlob(rows, problems))
    const link = document.createElement('a')
    link.href = url
    link.download = `${file.name.replace(/\.[^.]+$/, '')}-problems.xlsx`
    link.click()
    URL.revokeObjectURL(url)
  }

  async function openImport(id: number) {
    setError('')
    try {
      setResult(await accountingRequest(`/check-imports/${id}`))
      setPreview(null)
    } catch (err: any) {
      setError(err.message)
    }
  }

  async function downloadOriginal(row: any) {
    setError('')
    setDownloadingId(row.id)
    try {
      await downloadFrom(`/check-imports/${row.id}/file`, row.fileName, 'Could not download the file.')
    } catch (err: any) {
      setError(err.message)
    } finally {
      setDownloadingId(null)
    }
  }


  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 2 }}>
      <LeoHeader sx={{ mb: 3 }} />
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Import Check</Typography>

      <Typography variant="overline" color="info.main" sx={{ display: 'block', fontWeight: 700, mb: 1 }}>Recent imports (latest 10)</Typography>
      <TableContainer component={Card} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'info.main' }}>
              {[headCell('Import'), headCell('Posted at'), headCell('Posted by'), headCell('File'), headCell('Rows', true), headCell('Posted', true), headCell('Skipped', true), headCell('Total', true), headCell('Stored')]}
            </TableRow>
          </TableHead>
          <TableBody>
            {imports.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Link component="button" type="button" onClick={() => openImport(row.id)} sx={{ fontWeight: 700 }}>{row.id}</Link>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(row.postedAt)}</TableCell>
                <TableCell>{row.postedBy}</TableCell>
                <TableCell>
                  <Link component="button" type="button" title="Download the original file" disabled={downloadingId === row.id} onClick={() => downloadOriginal(row)} sx={{ fontWeight: 600 }}>
                    <DownloadLabel busy={downloadingId === row.id}>{row.fileName}</DownloadLabel>
                  </Link>
                </TableCell>
                <TableCell align="right">{row.rowCount}</TableCell>
                <TableCell align="right">{row.postedCount}</TableCell>
                <TableCell align="right" sx={{ color: row.skippedCount ? 'error.main' : undefined }}>{row.skippedCount}</TableCell>
                <TableCell align="right">{formatMoney(row.postedTotal)}</TableCell>
                <TableCell>{row.inS3 ? 'S3' : 'Leo only'}</TableCell>
              </TableRow>
            ))}
            {!imports.length ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary">No imports yet.</Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5 }}>
            <input ref={input} type="file" accept=".csv,.txt,.xlsx" hidden onChange={choose} />
            <Button variant="contained" color="warning" startIcon={<UploadFileIcon />} disabled={Boolean(busy)} onClick={() => input.current?.click()}>
              {busy === 'preview' ? 'Reading…' : 'Upload'}
            </Button>
            {file ? <Typography variant="body2" sx={{ fontWeight: 600 }}>{file.name}</Typography> : null}
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              color="secondary"
              disabled={!preview?.readyCount || Boolean(preview?.skippedCount) || Boolean(busy)}
              onClick={() => {
                setName('')
                setAsking(true)
              }}
            >
              Post client transactions
            </Button>
          </Stack>
          {busy ? <LinearProgress color="warning" sx={{ mt: 2 }} /> : null}
          {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
          {preview ? (
            <Box sx={{ mt: 2 }}>
              {preview.skippedCount ? (
                <Alert
                  severity="error"
                  sx={{ mb: 1.5, alignItems: 'center' }}
                  action={
                    <Button color="error" size="small" variant="outlined" onClick={downloadProblems}>
                      Download problem file
                    </Button>
                  }
                >
                  {preview.skippedCount} of {preview.rows.length} row{preview.rows.length === 1 ? '' : 's'} {preview.skippedCount === 1 ? 'has a problem' : 'have problems'}, so nothing can be posted. Download the problem file, fix the red rows and upload it again.
                </Alert>
              ) : (
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  {preview.readyCount} ready ({formatMoney(preview.readyTotal)})
                </Typography>
              )}
              <RowsTable rows={preview.rows} showStatus />
            </Box>
          ) : null}
          {result ? <ImportResult result={result} /> : null}
        </CardContent>
      </Card>

      <Dialog open={asking} onClose={busy ? undefined : () => setAsking(false)} maxWidth="xs" fullWidth>
        <Box component="form" onSubmit={post}>
          <DialogTitle sx={{ fontWeight: 700 }}>Post client transactions</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Adds {preview?.readyCount} credit{preview?.readyCount === 1 ? '' : 's'} for {formatMoney(preview?.readyTotal)}.
            </Alert>
            {/* The logged-in user is saved as who posted; the name is asked only when nobody is logged in. */}
            {loggedIn ? null : (
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="Your name"
                value={name}
                onChange={(event) => setName(event.target.value.slice(0, 60))}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAsking(false)} disabled={Boolean(busy)}>Cancel</Button>
            <Button type="submit" variant="contained" color="secondary" disabled={poster.length < 2 || Boolean(busy)}>
              {busy === 'post' ? 'Posting…' : 'Post'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  )
}
